import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useToast } from '@/components/ui/use-toast';
import { getSpotifyData } from '@/api/spotify';
import { Info } from 'lucide-react';
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslations } from '../hooks/useTranslations';
import CorporateNavbar from '@/components/CorporateNavbar';
import CorporateFooter from '@/components/CorporateFooter';
import EtherealBackground from '@/components/EtherealBackground';
import PlaylistGrid from '@/components/PlaylistGrid';

// Define types for Spotify data
interface SpotifyImage {
  url: string;
  height: number;
  width: number;
}

interface SpotifyAlbum {
  name: string;
  images: SpotifyImage[];
  release_date: string;
}

interface SpotifyTrack {
  id: string;
  name: string;
  duration_ms: number;
  external_urls: {
    spotify: string;
  };
  album: SpotifyAlbum;
  artists: {
    name: string;
  }[];
}

interface SpotifyData {
  latestTracks: SpotifyTrack[];
  topTracks: SpotifyTrack[];
}

const RECAPTCHA_SITE_KEY = '6Le5sS4rAAAAADbM6qalOJ7V0Er5HQC4L0k7fKQZ';
const EMAILJS_PUBLIC_KEY = 'py9itHZBqTCZ7lFgE';

const AIMusicDreams = () => {
  const { toast } = useToast();
  const t = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [spotifyData, setSpotifyData] = useState<SpotifyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmailJsLoaded, setIsEmailJsLoaded] = useState(false);
  const recaptchaRef = useRef<number | null>(null);
  const recaptchaWrapperRef = useRef<HTMLDivElement>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    socialPlatform: '',
    socialUsername: '',
    request: '',
    acceptTerms: false
  });
  
  const [showSocialUsername, setShowSocialUsername] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(true);
  
  // --- reCAPTCHA widget rendering fix ---
  useEffect(() => {
    let widgetId: number | null = null;

    const renderRecaptcha = () => {
      if (
        window.grecaptcha &&
        recaptchaWrapperRef.current &&
        recaptchaWrapperRef.current.childNodes.length === 0
      ) {
        widgetId = window.grecaptcha.render(recaptchaWrapperRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          theme: 'light',
        });
        recaptchaRef.current = widgetId;
      }
    };

    // Global callback för reCAPTCHA
    (window as any).onRecaptchaLoadCallback = () => {
      renderRecaptcha();
    };

    // Om scriptet redan är laddat
    if (window.grecaptcha && window.grecaptcha.render) {
      renderRecaptcha();
    }

    return () => {
      // Rensa widgeten
      if (widgetId !== null && window.grecaptcha) {
        window.grecaptcha.reset(widgetId);
      }
    };
  }, []);

  // EmailJS-scriptet laddas som tidigare
  useEffect(() => {
    if (!document.getElementById('emailjs-script')) {
      const emailJsScript = document.createElement('script');
      emailJsScript.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
      emailJsScript.async = true;
      emailJsScript.id = 'emailjs-script';
      emailJsScript.onload = () => {
        (window as any).emailjs.init(EMAILJS_PUBLIC_KEY);
        setIsEmailJsLoaded(true);
      };
      document.head.appendChild(emailJsScript);
    } else {
      setIsEmailJsLoaded(true);
    }
    // Ingen script-cleanup här
  }, []);
  
  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Show social username field when platform is selected
    if (name === 'socialPlatform' && value) {
      setShowSocialUsername(true);
    } else if (name === 'socialPlatform' && !value) {
      setShowSocialUsername(false);
      setFormData({
        ...formData,
        socialUsername: ''
      });
    }
  };
  
  // Handle checkbox change
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked
    });
  };
  
  const handleSongRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Validera formulär
    if (!formData.name || !formData.email || !formData.request || !formData.acceptTerms) {
      toast({
        title: 'Formulärfel',
        description: 'Vänligen fyll i alla obligatoriska fält och acceptera villkoren.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }
    
    // Validera e-postformatet
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: 'Ogiltig e-postadress',
        description: 'Vänligen ange en giltig e-postadress.',
        variant: 'destructive',
      });
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Verifiera reCAPTCHA
      if (!window.grecaptcha || recaptchaRef.current === null) {
        toast({
          title: 'CAPTCHA inte verifierad',
          description: 'Vänligen verifiera att du inte är en robot genom att klicka på CAPTCHA-rutan.',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }
      const recaptchaToken = window.grecaptcha.getResponse(recaptchaRef.current);
      if (!recaptchaToken) {
        toast({
          title: 'CAPTCHA inte verifierad',
          description: 'Vänligen verifiera att du inte är en robot genom att klicka på CAPTCHA-rutan.',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }
      // Förbered EmailJS-data
      const templateParams: Record<string, string> = {
        name: formData.name,
        email: formData.email,
        social_platform: formData.socialPlatform ? `${formData.socialPlatform}: ${formData.socialUsername}` : "Inte angiven",
        message: formData.request,
        'g-recaptcha-response': recaptchaToken,
      };
      
      // Skicka formuläret med EmailJS
      await (window as any).emailjs.send(
        'service_tdu2ydf', // EmailJS service ID
        'Aimusicdreams', // EmailJS template ID (matchar exakt EmailJS)
        templateParams
      );
      
      toast({
        title: 'Låtönskning mottagen!',
        description: 'Tack för din önskning. Vi har lagt till den i vår lista.',
      });
      
      // Återställ formuläret
      setFormData({
        name: '',
        email: '',
        socialPlatform: '',
        socialUsername: '',
        request: '',
        acceptTerms: false
      });
      setShowSocialUsername(false);
      // Reset reCAPTCHA
      if (window.grecaptcha && recaptchaRef.current !== null) {
        window.grecaptcha.reset(recaptchaRef.current);
      }
    } catch (error: unknown) {
      console.error('Fel vid skickande av formulär:', error);
      toast({
        title: 'Något gick fel',
        description: 'Vi kunde inte skicka din förfrågan just nu. Försök igen senare.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  useEffect(() => {
    const fetchSpotifyData = async () => {
      try {
        setIsLoading(true);
        console.log("Börjar hämta Spotify-data...");
        
        const data = await getSpotifyData();
        console.log("Spotify-data hämtad:", data);
        
        // Validera att vi fick den data vi förväntade oss
        if (!data.latestTracks || !data.topTracks) {
          console.error("Saknar data i Spotify-svaret:", data);
          toast({
            title: 'Dataproblem',
            description: 'Fick ofullständig data från Spotify API:et.',
            variant: 'destructive',
          });
        } else if (data.latestTracks.length === 0 && data.topTracks.length === 0) {
          console.log("Inga låtar hittades för artist ID:", "4mzl9kB6QtB7F2UZktujSY");
          toast({
            title: 'Inga låtar hittades',
            description: 'Hittade inga låtar för detta artist ID. Är det rätt ID?',
            variant: 'destructive',
          });
        }
        
        setSpotifyData(data);
      } catch (error) {
        console.error('Error fetching Spotify data:', error);
        toast({
          title: 'Ett fel uppstod',
          description: 'Kunde inte hämta låtar från Spotify. Försök igen senare.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSpotifyData();
  }, [toast]);
  
  // Format milliseconds to minutes:seconds
  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  // Get a genre type from the track name for presentation
  const getGenreFromTrack = (track: SpotifyTrack) => {
    const genres = ['Electronic', 'Ambient', 'Techno', 'Downtempo', 'Synthwave', 'House'];
    // Use the track ID to create a "random" but consistent genre
    const genreIndex = parseInt(track.id.slice(-2), 16) % genres.length;
    return genres[genreIndex];
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowSwitcher(window.scrollY < 120);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col relative"
      style={{ backgroundColor: '#FDFDFD' }}
    >
      <Helmet>
        <title>AIMusicDreams — AI-genererad musik | Aifinity</title>
        <meta name="description" content="AI-genererad dansbandsmusik och folkmusik skapad av Aifinity AB. Lyssna på Spotify och SoundCloud." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://aifinity.se/aimusicdreams" />
      </Helmet>
      <EtherealBackground />
      <div className="relative z-10">
        <CorporateNavbar />
        <div
          style={{
            transition: 'opacity 0.2s',
            opacity: showSwitcher ? 1 : 0,
            pointerEvents: showSwitcher ? 'auto' : 'none',
            position: 'fixed',
            top: '100px',
            right: '20px',
            zIndex: 40
          }}
        >
          <LanguageSwitcher variant="floating" />
        </div>
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 relative">
        <div className="container mx-auto px-4">
          <div 
            className="max-w-4xl mx-auto text-center p-12 rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(167, 84, 255, 0.1) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 8px 32px rgba(167, 84, 255, 0.15)'
            }}
          >
            <h1 className="sr-only">AIMusicDreams — AI-genererad musik av Aifinity</h1>
            <div className="flex justify-center mb-12">
              <img src="/AIMusicDreams-trasparent.png" alt="AIMusicDreams logotyp" className="mx-auto max-w-[540px] w-full md:max-w-[540px] sm:max-w-[300px]" />
            </div>
            <p className="text-xl text-gray-600 mb-10">
              {t.aimusicdreams.hero.description}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="https://open.spotify.com/artist/4mzl9kB6QtB7F2UZktujSY?si=Dl_Ky7l6SGqj8yw_f8k3mQ" target="_blank" rel="noopener noreferrer">
                <Button 
                  className="text-white px-6 py-5 w-full sm:w-auto rounded-xl transition-all hover:scale-105"
                  style={{
                    backgroundColor: '#A754FF',
                    boxShadow: '0 4px 16px rgba(167, 84, 255, 0.3)'
                  }}
                >
                  <img src="/spotify-alt-1-svgrepo-com.svg" alt="Spotify" className="mr-2 h-5 w-5 filter invert" />
                  {t.aimusicdreams.hero.spotify}
                </Button>
              </a>
              <a href="https://soundcloud.com/aimusicdreams" target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline" 
                  className="px-6 py-5 w-full sm:w-auto rounded-xl transition-all hover:scale-105"
                  style={{
                    borderColor: '#A754FF',
                    color: '#A754FF',
                    borderWidth: '2px'
                  }}
                >
                  <img src="/soundcloud-svgrepo-com.svg" alt="SoundCloud" className="mr-2 h-5 w-5" />
                  {t.aimusicdreams.hero.soundcloud}
                </Button>
              </a>
              <a href="https://www.instagram.com/aimusicdreams/" target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline" 
                  className="px-6 py-5 w-full sm:w-auto rounded-xl transition-all hover:scale-105"
                  style={{
                    borderColor: '#A754FF',
                    color: '#A754FF',
                    borderWidth: '2px'
                  }}
                >
                  <img src="/instagram-svgrepo-com.svg" alt="Instagram" className="mr-2 h-5 w-5" />
                  {t.aimusicdreams.hero.instagram}
                </Button>
              </a>
              <a href="https://www.tiktok.com/@aimusicdreams" target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline" 
                  className="px-6 py-5 w-full sm:w-auto rounded-xl transition-all hover:scale-105"
                  style={{
                    borderColor: '#A754FF',
                    color: '#A754FF',
                    borderWidth: '2px'
                  }}
                >
                  <img src="/tiktok-svgrepo-com.svg" alt="TikTok" className="mr-2 h-5 w-5" />
                  {t.aimusicdreams.hero.tiktok}
                </Button>
              </a>
              <a href="mailto:aimusicdreams@gmail.com">
                <Button 
                  variant="outline" 
                  className="px-6 py-5 w-full sm:w-auto rounded-xl transition-all hover:scale-105"
                  style={{
                    borderColor: '#A754FF',
                    color: '#A754FF',
                    borderWidth: '2px'
                  }}
                >
                  <img src="/mail-svgrepo-com.svg" alt="Email" className="mr-2 h-5 w-5" />
                  {t.aimusicdreams.hero.mail}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Latest Releases Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <div 
            className="max-w-6xl mx-auto p-12 rounded-3xl mb-12"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(167, 84, 255, 0.1) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 8px 32px rgba(167, 84, 255, 0.15)'
            }}
          >
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-900">
              {t.aimusicdreams.latest.heading}
            </h2>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#A754FF' }}></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5">
                {spotifyData?.latestTracks.slice(0, 5).map((track) => (
                  <div key={track.id} className="group p-2 sm:p-3 bg-white/40 backdrop-blur-md rounded-2xl transition-all duration-300 hover:shadow-lg hover:scale-105 border border-white/50">
                    <div className="aspect-square bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl flex items-center justify-center mb-2 sm:mb-3 relative overflow-hidden shadow-md">
                      {track.album.images && track.album.images.length > 0 ? (
                        <img 
                          src={track.album.images[0].url} 
                          alt={track.name} 
                          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-300"
                        />
                      ) : null}
                      <a 
                        href={track.external_urls.spotify} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="relative z-10 bg-white/80 backdrop-blur-sm p-2 sm:p-3 rounded-full transition-transform duration-300 group-hover:scale-110"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#A754FF' }}>
                          <polygon points="5 3 19 12 5 21 5 3"></polygon>
                        </svg>
                      </a>
                    </div>
                    <h3 className="text-base sm:text-lg font-bold mb-0.5 group-hover:text-[#A754FF] transition-colors truncate text-gray-900" title={track.name}>{track.name}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm">{getGenreFromTrack(track)}</p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 sm:mt-10 text-center">
                <a href="https://open.spotify.com/artist/4mzl9kB6QtB7F2UZktujSY?si=Dl_Ky7l6SGqj8yw_f8k3mQ" target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="text-white px-6 sm:px-8 py-4 sm:py-6 rounded-xl transition-all hover:scale-105"
                    style={{
                      backgroundColor: '#A754FF',
                      boxShadow: '0 4px 16px rgba(167, 84, 255, 0.3)'
                    }}
                  >
                    {t.aimusicdreams.latest.allSongs}
                  </Button>
                </a>
              </div>
            </>
          )}
          </div>
        </div>
      </section>
      
      {/* Top 10 Section */}
      <section className="py-16 sm:py-20 relative">
        <div className="container mx-auto px-4">
          <div 
            className="max-w-4xl mx-auto p-12 rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(167, 84, 255, 0.1) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 8px 32px rgba(167, 84, 255, 0.15)'
            }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-10 sm:mb-16 text-center text-gray-900">
              {t.aimusicdreams.top.heading}
            </h2>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: '#A754FF' }}></div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto">
              <div className="space-y-2 sm:space-y-4">
                {spotifyData?.topTracks.slice(0, 10).map((track, index) => (
                  <div key={track.id} className="bg-white/60 backdrop-blur-md border border-white/50 rounded-2xl flex items-center p-3 sm:p-4 group hover:shadow-lg transition-all duration-300">
                    <div className="mr-2 sm:mr-4 text-xl sm:text-2xl font-bold" style={{ color: '#A754FF' }}>{index < 9 ? `0${index + 1}` : index + 1}</div>
                    <div className="flex items-center">
                      {track.album.images && track.album.images.length > 0 && (
                        <div className="mr-2 sm:mr-3 w-8 h-8 sm:w-10 sm:h-10 rounded-xl overflow-hidden flex-shrink-0 border-2" style={{ borderColor: '#A754FF' }}>
                          <img 
                            src={track.album.images[0].url} 
                            alt={track.album.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="mr-2 sm:mr-3 p-1.5 sm:p-2 rounded-md bg-white/80">
                        <a 
                          href={track.external_urls.spotify}
                          target="_blank"
                          rel="noopener noreferrer" 
                          style={{ color: '#A754FF' }}
                          className="hover:opacity-80 block">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="sm:w-5 sm:h-5">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                          </svg>
                        </a>
                      </div>
                    </div>
                    <div className="flex-grow min-w-0">
                      <h3 className="font-bold text-gray-900 group-hover:text-[#A754FF] transition-colors truncate" title={track.name}>{track.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{getGenreFromTrack(track)} • {formatDuration(track.duration_ms)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      </section>
      
      {/* Song Request Section */}
      <section className="py-16 sm:py-20 relative">
        <div className="container mx-auto px-4">
          <div 
            className="max-w-3xl mx-auto p-12 rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(167, 84, 255, 0.1) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 8px 32px rgba(167, 84, 255, 0.15)'
            }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-center text-gray-900">
              {t.aimusicdreams.request.heading}
            </h2>
            <p className="text-gray-600 text-center text-sm sm:text-base mb-6 sm:mb-10">
              {t.aimusicdreams.request.description}
            </p>
            
            <form onSubmit={handleSongRequest} className="space-y-4 sm:space-y-5">
              {/* Namn */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  {t.aimusicdreams.request.name}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:border-[#A754FF] focus:outline-none focus:ring-2 focus:ring-[#A754FF] text-gray-900"
                  placeholder={t.aimusicdreams.request.namePlaceholder}
                />
              </div>
              
              {/* E-post */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  {t.aimusicdreams.request.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:border-[#A754FF] focus:outline-none focus:ring-2 focus:ring-[#A754FF] text-gray-900"
                  placeholder={t.aimusicdreams.request.emailPlaceholder}
                />
              </div>
              
              {/* Social Plattform */}
              <div>
                <label htmlFor="socialPlatform" className="block text-sm font-medium mb-2">
                  {t.aimusicdreams.request.socialPlatform}
                </label>
                <select
                  id="socialPlatform"
                  name="socialPlatform"
                  value={formData.socialPlatform}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:border-[#A754FF] focus:outline-none focus:ring-2 focus:ring-[#A754FF] text-gray-900"
                >
                  <option value="">{t.aimusicdreams.request.socialPlatformPlaceholder}</option>
                  <option value="tiktok">{t.aimusicdreams.request.tiktok}</option>
                  <option value="instagram">{t.aimusicdreams.request.instagram}</option>
                </select>
              </div>
              
              {/* Social användarnamn (visas endast om plattform är vald) */}
              {showSocialUsername && (
                <div>
                  <label htmlFor="socialUsername" className="block text-sm font-medium mb-2">
                    {formData.socialPlatform === 'tiktok' 
                      ? t.aimusicdreams.request.socialUsernameTikTok 
                      : t.aimusicdreams.request.socialUsernameInstagram}
                  </label>
                  <input
                    type="text"
                    id="socialUsername"
                    name="socialUsername"
                    value={formData.socialUsername}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:border-[#A754FF] focus:outline-none focus:ring-2 focus:ring-[#A754FF] text-gray-900"
                    placeholder={formData.socialPlatform === 'tiktok' 
                      ? t.aimusicdreams.request.socialUsernameTikTokPlaceholder 
                      : t.aimusicdreams.request.socialUsernameInstagramPlaceholder}
                  />
                </div>
              )}
              
              {/* Låtönskan */}
              <div>
                <label htmlFor="request" className="block text-sm font-medium mb-2">
                  {t.aimusicdreams.request.request}
                </label>
                <textarea
                  id="request"
                  name="request"
                  value={formData.request}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  maxLength={1000}
                  className="w-full px-4 py-3 bg-white/80 border border-gray-300 rounded-xl focus:border-[#A754FF] focus:outline-none focus:ring-2 focus:ring-[#A754FF] text-gray-900"
                  placeholder={t.aimusicdreams.request.requestPlaceholder}
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {formData.request.length}/1000 {t.aimusicdreams.request.charCount}
                </div>
              </div>
              
              {/* Villkor */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    name="acceptTerms"
                    checked={formData.acceptTerms}
                    onChange={handleCheckboxChange}
                    required
                    className="h-4 w-4 rounded border-gray-300 focus:ring-[#A754FF] text-[#A754FF] bg-white"
                  />
                </div>
                <div className="ml-3 relative group">
                  <label htmlFor="acceptTerms" className="text-sm text-gray-700 flex items-center">
                    {t.aimusicdreams.request.acceptTerms} *
                    <Info className="h-4 w-4 ml-1 cursor-help opacity-70" style={{ color: '#A754FF' }} />
                    <div className="absolute bottom-full left-0 mb-2 p-3 rounded-xl bg-white/95 backdrop-blur-md border border-gray-200 text-sm text-gray-700 w-64 invisible group-hover:visible shadow-lg z-10">
                      {t.aimusicdreams.request.termsTooltip}
                    </div>
                  </label>
                </div>
              </div>
              
              {/* reCAPTCHA container */}
              <div className="flex justify-center mt-2 mb-4">
                <div
                  className="g-recaptcha"
                  data-sitekey="6Le5sS4rAAAAADbM6qalOJ7V0Er5HQC4L0k7fKQZ"
                  data-theme="light"
                  id="recaptcha-container"
                ></div>
              </div>
              
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="text-white px-6 sm:px-8 py-4 sm:py-6 w-full rounded-xl mt-4 transition-all hover:scale-105"
                style={{
                  backgroundColor: '#A754FF',
                  boxShadow: '0 4px 16px rgba(167, 84, 255, 0.3)'
                }}
              >
                {isSubmitting ? t.aimusicdreams.request.submitting : t.aimusicdreams.request.submit}
              </Button>
              
              <p className="text-xs text-gray-500 text-center">
                {t.aimusicdreams.request.recaptcha}
              </p>
            </form>
          </div>
        </div>
      </section>
      
      {/* About Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <div 
            className="max-w-6xl mx-auto p-12 rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(167, 84, 255, 0.1) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 8px 32px rgba(167, 84, 255, 0.15)'
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden border-2 border-white/50 shadow-lg relative z-10">
                  <img 
                    src="/collageAIM.png" 
                    alt="AIMusicDreams Artist Collage" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
              
              <div>
                <h2 className="text-3xl font-bold mb-6 text-gray-900">
                  {t.aimusicdreams.about.heading}
                </h2>
                <p className="text-gray-600 mb-6">
                  {t.aimusicdreams.about.p1}
                </p>
                <p className="text-gray-600 mb-6">
                  {t.aimusicdreams.about.p2}
                </p>
                <p className="text-gray-600 mb-8">
                  {t.aimusicdreams.about.p3}
                </p>
                <div className="flex space-x-4">
                  <a href="https://www.instagram.com/aimusicdreams/" target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-[#A754FF] transition-colors">
                    <img src="/instagram-svgrepo-com.svg" alt="Instagram" className="mr-2 h-6 w-6" />
                    {t.aimusicdreams.about.instagram}
                  </a>
                  <a href="https://www.tiktok.com/@aimusicdreams" target="_blank" rel="noopener noreferrer" className="flex items-center text-gray-700 hover:text-[#A754FF] transition-colors">
                    <img src="/tiktok-svgrepo-com.svg" alt="TikTok" className="mr-2 h-6 w-6" />
                    {t.aimusicdreams.about.tiktok}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Playlist Grid Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4">
          <div
            className="max-w-6xl mx-auto p-12 rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(167, 84, 255, 0.1) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.6)',
              boxShadow: '0 8px 32px rgba(167, 84, 255, 0.15)'
            }}
          >
            <PlaylistGrid />
          </div>
        </div>
      </section>

      <CorporateFooter />
      </div>
    </div>
  );
};

export default AIMusicDreams; 